// Theme: light | dark | system. Persists to localStorage and reflects on
// the document's class list so Tailwind's `dark:` modifier engages.
//
// Reads on mount once; the inline boot script in index.html applies the
// stored preference before React hydrates to avoid a flash of wrong theme.

import { useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";

const KEY = "cornholio.theme";

function systemPref(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function readStored(): Theme {
  // Default to "light" so the navy-on-transparent Cornholio.app
  // wordmark is always readable on the dashboard / auth pages.
  // Users can still flip to dark or system via the switcher.
  if (typeof window === "undefined") return "light";
  const v = window.localStorage.getItem(KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "light";
}

function apply(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // "system" means: defer to OS preference. Remove both classes so the
  // @media (prefers-color-scheme) fallback in CSS engages. Explicit
  // picks add a class so the override block in CSS wins regardless of
  // the OS setting.
  root.classList.remove("light");
  root.classList.remove("dark");
  if (theme === "light") root.classList.add("light");
  if (theme === "dark") root.classList.add("dark");
  const resolved = theme === "system" ? systemPref() : theme;
  root.style.colorScheme = resolved;
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => readStored());

  // Re-apply when the user toggles, or when the OS theme changes if the
  // user is on "system."
  useEffect(() => {
    apply(theme);
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = (next: Theme) => {
    window.localStorage.setItem(KEY, next);
    setThemeState(next);
  };

  return { theme, setTheme };
}

// Run once at module load on the client so the theme applies even before
// any component mounts (paired with the inline boot script in index.html).
if (typeof window !== "undefined") {
  apply(readStored());
}
